import { useAuthStore } from "@/kibi/modules/auth/domain/store/auth_store";
import type { NavigationGuardNext, RouteLocationNormalized } from "vue-router";

/* *
 * Authentication guard that protects routes based on authentication state
 *
 * Logic:
 * - If user is authenticated (has token):
 *   - Trying to access public auth pages (login, etc.)  redirect to home
 *   - Trying to access protected pages  allow
 *
 * - If user is NOT authenticated:
 *   - Trying to access protected pages  redirect to login
 *   - Trying to access public pages  allow */
export function authGuard(
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext,
) {
  const authStore = useAuthStore();
  const isAuthenticated = !!authStore.token;

  // Check if route requires authentication
  const requiresAuth = to.matched.some((record) => record.meta.requiresAuth);

  // Check if route is public (like login, forgot password, etc.)
  const isPublicRoute = to.matched.some((record) => record.meta.public);

  if (isAuthenticated) {
    // User is logged in
    if (isPublicRoute && to.path.startsWith("/auth")) {
      // Trying to access auth pages while logged in  redirect to home
      // Exception: allow access to 'acess-denied' even if authenticated
      if (to.path === "/auth/access-denied") {
        next();
      } else {
        next({ name: "dashboard" });
      }
    } else {
      // Allow access to protected pages
      next();
    }
  } else {
    // User is NOT logged in
    if (requiresAuth) {
      // Trying to access protected page  redirect to login
      next({
        name: "auth-login",
        query: { redirect: to.fullPath }, // Save intended destination
      });
    } else {
      // Allow access to public pages
      next();
    }
  }
}
